// src/components/inputs/EquationInput.js
import React, { useRef, useState, useCallback } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";

const INDENT = 36;
const PANEL_WIDTH_DESKTOP = 250;

/** Przybliżenie liczby dziesiętnej do ułamka o małym mianowniku (do podglądu).
 *  - np. 2.5 -> 5/2, 3.75 -> 15/4
 *  - tylko gdy błąd < 1e-10 i mianownik <= 24
 */
function approxRat(x, maxDen = 24, eps = 1e-10) {
  if (!Number.isFinite(x)) return null;
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);

  if (Math.abs(ax - Math.round(ax)) < eps) {
    return { num: sign * Math.round(ax), den: 1 };
  }

  let h1 = 1,
    h0 = 0;
  let k1 = 0,
    k0 = 1;
  let b = ax;

  for (let i = 0; i < 30; i++) {
    const a = Math.floor(b);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;

    const approx = h2 / k2;
    if (Math.abs(approx - ax) < eps) {
      return { num: sign * h2, den: k2 };
    }

    h0 = h1;
    h1 = h2;
    k0 = k1;
    k1 = k2;

    const frac = b - a;
    if (frac < eps) break;
    b = 1 / frac;
  }

  for (let den = 2; den <= maxDen; den++) {
    const num = Math.round(ax * den);
    if (Math.abs(num / den - ax) < eps) {
      return { num: sign * num, den };
    }
  }

  return null;
}

/**
 * Szuka pierwszego slasha "/" na top-level
 * (czyli NIE w nawiasach (), {} ani []).
 */
function findTopLevelSlash(s) {
  let par = 0,
    br = 0,
    sq = 0;

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "(") par++;
    else if (ch === ")") par = Math.max(0, par - 1);
    else if (ch === "{") br++;
    else if (ch === "}") br = Math.max(0, br - 1);
    else if (ch === "[") sq++;
    else if (ch === "]") sq = Math.max(0, sq - 1);
    else if (ch === "/" && par === 0 && br === 0 && sq === 0) {
      return i;
    }
  }
  return -1;
}

/** Podgląd LaTeX:
 *  - zachowuje styl usera: dziesiętne zostają dziesiętne, ułamki zostają ułamkami
 *  - zamienia mnożenie na \cdot, ale NIE skleja 0.8*0.9x
 *  - przecinek dziesiętny pokazuje jako "{,}" (KaTeX-friendly), jeśli user użył przecinka
 */
export function toLatexFraction(str) {
  const raw = String(str ?? "");
  const userUsedFraction = raw.includes("/");

  // roboczo do regexów: zamień przecinek -> kropka (ALE finalnie przywrócimy "{,}" jeśli user pisał przecinkiem)
  let work = raw.replace(/,/g, ".");

  // 0) usuń znak mnożenia TYLKO przy mnożeniu przez x (np. 5*x -> 5x, (m+1)*x -> (m+1)x)
  //    NIE usuwamy mnożenia między liczbami (np. 0.8*0.9x), bo to psuło podgląd
  work = work.replace(/(\d|\))\s*(\*|·)\s*(?=x\b)/g, "$1");

  // 1) mieszane ułamki: "3 1/2" -> "3\\dfrac{1}{2}"
  work = work.replace(
    /(-?\d+)\s+(\d+)\/(\d+)/g,
    (_, w, n, d) => `${w}\\dfrac{${n}}{${d}}`
  );

  // 2) zwykłe ułamki liczbowe: "-3/4" -> "\\dfrac{-3}{4}" (nie ruszaj już \\dfrac{...})
  work = work.replace(
    /(?<!\\dfrac\{)(-?\d+)\/(\d+)/g,
    (_, n, d) => n.startsWith("-")
      ? `-\\dfrac{${n.slice(1)}}{${d}}`
      : `\\dfrac{${n}}{${d}}`
  );

  // 3) ułamki z x w liczniku: x/2, -x/2, 5x/2, -12x/7
  work = work.replace(
    /(?<!\\dfrac\{)([+\-]?(?:\d+)?x)\/(\d+)/g,
    (_, num, den) => `\\dfrac{${num}}{${den}}`
  );

  // 4) ułamki typu (m+1)x/2, (2m-1)x/2, (a-b)x/3 (bez zagnieżdżonych nawiasów)
  work = work.replace(
    /(?<!\\dfrac\{)([+\-]?\([^()]*\)x)\/(\d+)/g,
    (_, num, den) => `\\dfrac{${num}}{${den}}`
  );

  // 5) wielocyfrowe wykładniki: 2^36 -> 2^{36}, 2^-12 -> 2^{-12}
  work = work.replace(/\^(-?\d{2,})/g, "^{$1}");

  // 6) funkcje trygonometryczne (ładniejszy podgląd)
  work = work.replace(/\bcos(?=\s*\()/gi, "\\cos");
  work = work.replace(/\bsin(?=\s*\()/gi, "\\sin");
  work = work.replace(/\btan(?=\s*\()/gi, "\\tan");
  work = work.replace(/\bcot(?=\s*\()/gi, "\\cot");

  work = work.replace(/sqrt\(([^)]+)\)/gi, "\\sqrt{$1}");
  work = work.replace(/sqrt([a-z0-9]+)/gi, "\\sqrt{$1}");
  work = work.replace(/π/g, "\\pi");
  work = work.replace(/\bpi\b/gi, "\\pi");

  // 7) dziesiętne -> ułamek TYLKO jeśli user i tak użył "/" (czyli jest w trybie ułamkowym)
  if (userUsedFraction) {
    work = work.replace(/([+\-]?\d+)\.(\d+)/g, (full, _intPart, fracPart) => {
      const numVal = Number(full);
      const rat = approxRat(numVal, 24, 1e-10);
      if (!rat || rat.den === 1) return full;
      if (String(fracPart).length >= 3 && rat.den > 8) return full;
      return `\\dfrac{${rat.num}}{${rat.den}}`;
    });
  }

  // 8) top-level licznik/mianownik: jeśli nadal mamy "/" na wierzchu, to opakuj CAŁOŚĆ w \\dfrac{...}{...}
  const idx = findTopLevelSlash(work);
  if (idx !== -1 && !work.includes("\\dfrac") && !work.includes("=")) {
    const left = work.slice(0, idx).trim();
    const right = work.slice(idx + 1).trim();
    if (left && right) {
      work = `\\dfrac{${left}}{${right}}`;
    }
  }

  // 9) finalnie: pokaż mnożenie jako \cdot (zostawiamy kolejność: najpierw ułamki, potem mnożenie)
  work = work.replace(/\*/g, "\\cdot ");
  work = work.replace(/·/g, "\\cdot ");

  // 10) jeśli user pisał przecinkiem (i nie mieszał z kropką), pokaż przecinek jako "{,}" dla KaTeX
  if (raw.includes(",") && !raw.includes(".")) {
    work = work.replace(/\./g, "{,}");
  }

  return work;
}

function setCaret(el, pos) {
  if (!el) return;
  requestAnimationFrame(() => el.setSelectionRange(pos, pos));
}

function valueToRaw(v) {
  if (typeof v === "string") return v;
  if (v == null) return "";
  if (typeof v === "object") {
    if (typeof v.raw === "string") return v.raw;
    if (v.raw && typeof v.raw === "object" && typeof v.raw.raw === "string") {
      return v.raw.raw;
    }
    for (const key of ["value", "text", "input", "expr", "equation", "latex"]) {
      if (typeof v[key] === "string") return v[key];
    }
    return "";
  }
  return String(v);
}

export default function EquationInput({
  value,
  onChange,
  disabled,
  placeholder,
  showPreview = true,
  label = "",
  suffixLatex = "",
  /** opcjonalnie: własna baza, np. "200px" albo "22ch" */
  basis,
}) {
  const inputRef = useRef(null);
  const [kbOpen, setKbOpen] = useState(false);

  const handleChange = useCallback(
    (e) => onChange && onChange(e.target.value),
    [onChange]
  );

  const handleKbAction = useCallback(
    (type, payload) => {
      const el = inputRef.current;
      if (!el) return;

      const text = valueToRaw(value);
      const start = el.selectionStart ?? text.length;
      const end = el.selectionEnd ?? text.length;

      const replace = (insertStr, caretShift = insertStr.length) => {
        const next = text.slice(0, start) + insertStr + text.slice(end);
        onChange && onChange(next);
        setCaret(el, start + caretShift);
      };

      if (type === "insert") {
        replace(String(payload ?? ""));
        return;
      }
      if (type === "backspace") {
        if (start !== end) {
          const next = text.slice(0, start) + text.slice(end);
          onChange && onChange(next);
          setCaret(el, start);
        } else if (start > 0) {
          const next = text.slice(0, start - 1) + text.slice(end);
          onChange && onChange(next);
          setCaret(el, start - 1);
        }
        return;
      }
      if (type === "clear") {
        onChange && onChange("");
        setCaret(el, 0);
        return;
      }
      if (type === "moveLeft") {
        setCaret(el, Math.max(0, Math.min(start, end) - 1));
        return;
      }
      if (type === "moveRight") {
        setCaret(el, Math.min(text.length, Math.max(start, end) + 1));
        return;
      }
      if (type === "toggle") {
        setKbOpen((o) => !o);
        return;
      }
    },
    [value, onChange]
  );

  const basisValue = basis || `var(--eq-input-basis, ${PANEL_WIDTH_DESKTOP}px)`;
  const rawValue = valueToRaw(value);

  const hasPreview = showPreview && rawValue !== "";
  const previewCore = hasPreview ? toLatexFraction(rawValue) : "";
  const previewLatex = hasPreview
    ? `${label ? `${label} = ` : ""}${previewCore}${suffixLatex ? `\\,${suffixLatex}` : ""}`
    : "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: hasPreview || kbOpen ? 8 : 6,
        alignItems: "stretch",
        width: "100%",
        maxWidth: `calc(${basisValue} + ${INDENT}px)`,
      }}
    >
      {/* Rząd: przełącznik klawiatury + pole tekstowe */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${INDENT}px minmax(0, 1fr)`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <SetKeyboard
          targetRef={inputRef}
          open={kbOpen}
          onOpenChange={setKbOpen}
          render="toggle"
          onAction={handleKbAction}
        />

        <input
          ref={inputRef}
          type="text"
          value={rawValue}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder || "np. (x-3/4)^2 + 1/2"}
          className="input-secondary"
          style={{
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Panel klawiatury */}
      {kbOpen && (
        <div style={{ width: "100%", marginTop: 2 }}>
          <SetKeyboard
            targetRef={inputRef}
            open={kbOpen}
            onOpenChange={setKbOpen}
            render="panel"
            panelIndent={INDENT}
            style={{ width: "100%" }}
            stretch={false}
            onAction={handleKbAction}
          />
        </div>
      )}

      {/* Podgląd LaTeX */}
      {hasPreview ? (
        <div style={{ marginTop: 2 }}>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={previewLatex} />
        </div>
      ) : null}
    </div>
  );
}
